import React from 'react';
import styled from "styled-components";
import {Alert} from "antd";
import {useIntl, defineMessages} from "react-intl";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {ITariffPackage} from "../../../../../store/reducers/tariffReducer/tariffReducerTypes";
import dayjs from "dayjs";

const GracePeriodBannerMessages = defineMessages({
  title: {
    id: "gracePeriodBanner.title",
    defaultMessage: "Payment Required",
  },
  message: {
    id: "gracePeriodBanner.message",
    defaultMessage: "Your subscription invoice was not paid. You have {daysRemaining} day{plural} to update your payment method.",
  },
  contactSupport: {
    id: "gracePeriodBanner.contactSupport",
    defaultMessage: "Contact Support",
  },
  updatePaymentMethod: {
    id: "gracePeriodBanner.updatePaymentMethod",
    defaultMessage: "Update Payment Method",
  },
});

const BannerContainer = styled.div`
  margin-bottom: 24px;
`;

const BannerActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

interface IGracePeriodBanner {
  tariffPackage: ITariffPackage;
  onUpdatePaymentMethod: () => void;
  onContactSupport?: () => void;
}

export const GracePeriodBanner: React.FC<IGracePeriodBanner> = ({
  tariffPackage,
  onUpdatePaymentMethod,
  onContactSupport,
}) => {
  const intl = useIntl();

  if (!tariffPackage.gracePeriodUntil) {
    return null;
  }

  const gracePeriodEnd = dayjs(tariffPackage.gracePeriodUntil);
  const now = dayjs();
  const daysRemaining = Math.max(0, Math.ceil(gracePeriodEnd.diff(now, 'day', true)));

  if (daysRemaining <= 0) {
    return null; // Grace period ended, should show blocked screen instead
  }

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport();
    } else {
      // Default: open mailto link
      window.location.href = 'mailto:support@jetsnode.com?subject=Payment Issue';
    }
  };

  return (
    <BannerContainer>
      <Alert
        message={intl.formatMessage(GracePeriodBannerMessages.title)}
        description={
          <>
            <div>
              {intl.formatMessage(GracePeriodBannerMessages.message, {
                daysRemaining,
                plural: daysRemaining !== 1 ? 's' : '',
              })}
            </div>
            <BannerActions>
              <CustomButton
                type="primary"
                onClick={onUpdatePaymentMethod}
              >
                {intl.formatMessage(GracePeriodBannerMessages.updatePaymentMethod)}
              </CustomButton>
              <CustomButton
                onClick={handleContactSupport}
              >
                {intl.formatMessage(GracePeriodBannerMessages.contactSupport)}
              </CustomButton>
            </BannerActions>
          </>
        }
        type="warning"
        showIcon
        closable={false}
      />
    </BannerContainer>
  );
};
